import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, currency, STATUS_ORDER, STATUS_CONFIG } from "../lib/api";
import { useSettings } from "../lib/SettingsContext";
import { generateOrderPDF } from "../lib/orderPdf";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Loader2, Check, Download, Info, ShieldCheck, AlertTriangle, Upload, Trash2, FileImage } from "lucide-react";
import { toast } from "sonner";

// Public, no-auth order panel. Reached by scanning the QR / opening the link
// in the PDF quote. Accessed by folio number only (>= 1001). Legacy orders
// (`o_...`) are not exposed here.
export default function PublicOrderView() {
  const { orderNumber } = useParams();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/public/orders/by-number/${orderNumber}`);
      setOrder(data);
      setNotFound(false);
    } catch (_e) {
      setNotFound(true);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    if (!order || approving) return;
    setApproving(true);
    try {
      const { data } = await axios.post(`${API}/public/orders/by-number/${orderNumber}/approve`);
      setOrder(data);
      toast.success("¡Pedido autorizado! Comenzaremos con la producción.");
    } catch (e) {
      toast.error("No se pudo autorizar el pedido. Contáctanos por WhatsApp.");
    } finally {
      setApproving(false);
    }
  };

  const downloadPdf = async () => {
    try { await generateOrderPDF(order, settings); }
    catch { toast.error("No se pudo generar el PDF"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (notFound || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-6" data-testid="public-order-not-found">
        <div className="max-w-md text-center bg-white border-2 border-black p-8">
          <AlertTriangle className="w-10 h-10 mx-auto text-[#FF3B30]" />
          <h1 className="font-display text-3xl tracking-tight mt-4">Pedido no encontrado</h1>
          <p className="text-sm text-neutral-600 mt-2">
            El folio <span className="font-mono font-bold">#{orderNumber}</span> no está disponible.
            Verifica el enlace o contáctanos.
          </p>
          <Link to="/contacto" className="mt-6 inline-block text-xs font-mono uppercase underline">
            Ir a contacto
          </Link>
        </div>
      </div>
    );
  }

  const currentIdx = STATUS_ORDER.indexOf(order.status);
  const approved = !!order.customer_approved;
  const brand = settings?.brand_name || "PRINTLAB";

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="public-order-page">
      {/* Top bar (self-contained, no navbar) */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-1">
            <span className="font-display text-2xl tracking-tight">{brand.toUpperCase()}</span>
            <span className="font-display text-2xl" style={{ color: settings?.brand_accent || "#FF3B30" }}>.</span>
          </div>
          <div className="text-xs font-mono uppercase text-neutral-500">Panel del cliente</div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8">
        {/* Folio + status */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono uppercase text-neutral-500">Folio de pedido</div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter mt-1" data-testid="public-order-folio">
              #{order.order_number}
            </h1>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <StatusBadge status={order.status} />
              <span className="text-xs font-mono text-neutral-500">
                {new Date(order.created_at).toLocaleString("es-MX", { hour12: false })}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-mono uppercase text-neutral-500">Total</div>
            <div className="font-display text-4xl md:text-5xl tracking-tight">{currency(order.total)}</div>
          </div>
        </div>

        {/* Authorization banner (top priority) */}
        {!approved ? (
          <div
            className="mt-6 border-2 border-[#FF3B30] bg-red-50 p-5 md:p-6"
            data-testid="public-order-auth-banner"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-[#FF3B30] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-2xl md:text-3xl tracking-tight">
                  Necesitamos tu autorización
                </div>
                <p className="text-sm md:text-base text-neutral-700 mt-2">
                  Para iniciar la producción de tu pedido necesitamos que revises los detalles y confirmes que todo esté correcto.
                  Una vez autorizado, procederemos a fabricarlo.
                </p>
                <div className="mt-4 flex gap-3 flex-wrap">
                  <Button
                    onClick={approve}
                    disabled={approving}
                    className="rounded-none bg-black text-white hover:bg-[#FF3B30] h-12 px-6 text-base"
                    data-testid="public-order-approve-btn"
                  >
                    {approving ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <ShieldCheck className="w-5 h-5 mr-2" />
                        Autorizar pedido
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadPdf}
                    className="rounded-none border-black hover:bg-black hover:text-white h-12 px-6 text-base"
                    data-testid="public-order-pdf-btn"
                  >
                    <Download className="w-5 h-5 mr-2" /> Descargar PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="mt-6 border-2 border-emerald-600 bg-emerald-50 p-5"
            data-testid="public-order-approved-banner"
          >
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-display text-xl md:text-2xl tracking-tight text-emerald-900">
                  Pedido autorizado ✓
                </div>
                <p className="text-sm text-emerald-800 mt-1">
                  Autorizado el {new Date(order.customer_approved_at).toLocaleString("es-MX", { hour12: false })}.
                  Ya estamos procesando tu pedido.
                </p>
                <Button
                  variant="outline"
                  onClick={downloadPdf}
                  className="mt-4 rounded-none border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white"
                  data-testid="public-order-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Descargar PDF
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bank accounts for 50% deposit — only shown while deposit isn't paid */}
        {!approved && !order.deposit_paid && (settings?.bank_name || settings?.bank_clabe) && (
          <div className="mt-6 border-2 border-black bg-white p-5 md:p-6" data-testid="public-order-bank-block">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-black text-white flex items-center justify-center font-display text-xl flex-shrink-0">$</div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl md:text-2xl tracking-tight">Datos para transferir tu anticipo</div>
                <p className="text-sm text-neutral-700 mt-1">
                  <span className="font-bold">Para procesar tu pedido se requiere el 50% de anticipo.</span>{" "}
                  Transfiere a cualquiera de los siguientes datos y envíanos el comprobante por WhatsApp.
                </p>

                {/* Deposit amount pill */}
                {Number(order.deposit_amount || order.total * 0.5) > 0 && (
                  <div className="mt-4 inline-flex items-baseline gap-2 bg-amber-100 border-2 border-amber-500 px-3 py-2">
                    <span className="text-[10px] font-mono uppercase text-amber-900">Anticipo</span>
                    <span className="font-display text-2xl text-amber-900">
                      {currency(order.deposit_amount || order.total * 0.5)}
                    </span>
                  </div>
                )}

                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {settings.bank_name && (
                    <BankRow label="Banco" value={settings.bank_name} testid="bank-name" />
                  )}
                  {settings.bank_beneficiary && (
                    <BankRow label="Beneficiario" value={settings.bank_beneficiary} testid="bank-beneficiary" />
                  )}
                  {settings.bank_clabe && (
                    <BankRow label="CLABE / Cuenta" value={settings.bank_clabe} testid="bank-clabe" mono />
                  )}
                  {settings.bank_email && (
                    <BankRow label="Email de pagos" value={settings.bank_email} testid="bank-email" />
                  )}
                </div>

                {(settings?.company_whatsapp || settings?.company_phone) && (
                  <a
                    href={`https://wa.me/${(settings.company_whatsapp || settings.company_phone).replace(/\D/g, "")}?text=${encodeURIComponent(`Hola, aquí envío el comprobante del anticipo de mi pedido #${order.order_number} por ${currency(order.deposit_amount || order.total * 0.5)}.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 bg-emerald-600 text-white px-4 h-10 hover:bg-black transition-colors text-sm font-medium"
                    data-testid="public-order-send-receipt-whatsapp"
                  >
                    <Upload className="w-4 h-4" /> Enviar comprobante por WhatsApp
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Timeline */}
            <div className="bg-white border-2 border-black p-6">
              <h2 className="font-display text-xl tracking-tight mb-4">Seguimiento</h2>
              <div className="relative pl-6">
                <div className="absolute left-0 top-2 bottom-2 w-px bg-neutral-200" />
                {STATUS_ORDER.map((s, i) => {
                  const done = i <= currentIdx;
                  const current = i === currentIdx;
                  const cfg = STATUS_CONFIG[s];
                  return (
                    <div key={s} className="relative py-2.5">
                      <div
                        className="absolute -left-6 top-3 w-6 h-6 rounded-full border-2 flex items-center justify-center"
                        style={{
                          borderColor: done ? cfg.color : "#E5E5E5",
                          background: done ? cfg.color : "#FFFFFF",
                        }}
                      >
                        {done && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className={`font-medium text-sm ${current ? "text-black" : done ? "text-neutral-600" : "text-neutral-400"}`}>
                        {cfg.label}
                      </div>
                      {current && <div className="text-xs text-neutral-500 mt-0.5">Estado actual</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Items */}
            <div className="bg-white border-2 border-black p-6">
              <h2 className="font-display text-xl tracking-tight mb-1">Artículos del pedido</h2>
              <p className="text-xs text-neutral-500 mb-4">
                Revisa que cada diseño, color y cantidad sean correctos antes de autorizar.
              </p>
              <div className="space-y-4">
                {(order.items || []).map((it, i) => (
                  <div key={i} className="flex gap-4 border-b border-neutral-200 last:border-0 pb-4 last:pb-0">
                    <div className="w-20 h-20 bg-neutral-100 flex-shrink-0 relative overflow-hidden">
                      {it.product_image && (
                        <img src={it.product_image} alt={it.product_name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{it.product_name}</div>
                      <div className="text-xs font-mono text-neutral-500 mt-1">
                        {it.color_label && `Color: ${it.color_label} · `}
                        {it.size_label && `Talla: ${it.size_label} · `}
                        Cant: {it.quantity}
                      </div>
                      {it.printing_method_label && (
                        <div className="text-xs font-mono text-neutral-600 mt-1">Impresión: {it.printing_method_label}</div>
                      )}
                      {it.design_url ? (
                        <div className="mt-2 flex items-center gap-3">
                          <a
                            href={`${API}/files/${it.design_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative inline-block"
                            data-testid={`public-order-design-${i}`}
                            title="Ver diseño autorizado en tamaño real"
                          >
                            <div className="w-20 h-20 bg-neutral-50 border-2 border-black overflow-hidden">
                              <img
                                src={`${API}/files/${it.design_url}`}
                                alt={`Diseño ${it.design_filename || i + 1}`}
                                className="w-full h-full object-contain"
                                style={{ mixBlendMode: "multiply" }}
                              />
                            </div>
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          </a>
                          <div className="text-xs">
                            <div className="font-mono uppercase text-neutral-500 text-[10px]">Diseño autorizado</div>
                            <div className="font-medium">{it.design_filename || "diseño.png"}</div>
                            <a
                              href={`${API}/files/${it.design_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] font-mono uppercase underline text-neutral-600 hover:text-black"
                            >
                              Abrir / descargar
                            </a>
                          </div>
                        </div>
                      ) : it.design_filename ? (
                        <div className="text-xs text-neutral-500 mt-1">Diseño: {it.design_filename}</div>
                      ) : null}
                      {it.notes && <div className="text-xs italic text-neutral-500 mt-1">"{it.notes}"</div>}
                    </div>
                    <div className="font-display text-lg">{currency(it.line_total)}</div>
                  </div>
                ))}
                {(order.custom_items || []).map((ci, i) => (
                  <div key={`c-${i}`} className="flex gap-4 border-b border-neutral-200 last:border-0 pb-4 last:pb-0">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{ci.name}</div>
                      {ci.description && <div className="text-xs text-neutral-500 mt-1">{ci.description}</div>}
                      <div className="text-xs font-mono text-neutral-500 mt-1">Cant: {ci.quantity} · {currency(ci.unit_price)} c/u</div>
                    </div>
                    <div className="font-display text-lg">{currency(ci.line_total)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Admin-uploaded design mockups for customer to review before approving */}
            {Array.isArray(order.admin_designs) && order.admin_designs.length > 0 && (
              <div className="bg-white border-2 border-black p-6" data-testid="public-order-admin-designs">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
                  <h2 className="font-display text-xl tracking-tight">Diseños para tu aprobación</h2>
                  <span className="text-[10px] font-mono uppercase bg-[#FF3B30] text-white px-1.5 py-0.5">
                    Revisar antes de autorizar
                  </span>
                </div>
                <p className="text-xs text-neutral-500 mb-4">
                  Nuestro equipo preparó estos diseños. Ábrelos en grande para revisar cada detalle y luego autoriza tu pedido.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {order.admin_designs.map((d, i) => {
                    const url = `${API}/files/${d.storage_path}`;
                    const isImg = (d.content_type || "").startsWith("image/");
                    return (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group border-2 border-black bg-white p-3 hover:bg-neutral-50 transition-colors"
                        data-testid={`public-admin-design-${i}`}
                      >
                        <div className="w-full h-56 bg-neutral-50 flex items-center justify-center overflow-hidden">
                          {isImg ? (
                            <img
                              src={url}
                              alt={d.label || d.filename}
                              className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform"
                              style={{ mixBlendMode: "multiply" }}
                            />
                          ) : (
                            <div className="text-center">
                              <FileImage className="w-12 h-12 mx-auto text-neutral-500" />
                              <div className="text-xs font-mono mt-2">{d.filename}</div>
                            </div>
                          )}
                        </div>
                        {d.label && <div className="font-medium text-sm mt-2">{d.label}</div>}
                        <div className="text-[10px] font-mono text-neutral-500 truncate">{d.filename}</div>
                        {d.note && (
                          <div className="text-xs text-neutral-700 italic mt-1 bg-amber-50 border border-amber-200 px-2 py-1">
                            "{d.note}"
                          </div>
                        )}
                        <div className="text-[10px] font-mono uppercase text-neutral-600 mt-2 underline group-hover:text-black">
                          Ver en grande / descargar
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Customer attachments: upload up to 2 extra design files + measurements */}
            <AttachmentsBlock
              order={order}
              orderNumber={orderNumber}
              approved={approved}
              onUpdate={setOrder}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-white border-2 border-black p-5">
              <div className="text-xs font-mono uppercase text-neutral-500">Resumen</div>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span>{currency(order.subtotal)}</span></div>
                {order.delivery_method === "sucursal" ? (
                  <div className="flex justify-between"><span>Entrega</span><span>Recolección</span></div>
                ) : (
                  <div className="flex justify-between"><span>Envío</span><span>{order.shipping_fee === 0 ? "Gratis" : currency(order.shipping_fee)}</span></div>
                )}
                {order.iva_enabled && Number(order.iva_amount || 0) > 0 && (
                  <div className="flex justify-between"><span>IVA</span><span>{currency(order.iva_amount)}</span></div>
                )}
                <div className="flex justify-between font-display text-xl pt-2 border-t border-neutral-200">
                  <span>Total</span><span>{currency(order.total)}</span>
                </div>
              </div>
            </div>

            {/* Delivery */}
            <div className="bg-white border-2 border-black p-5">
              <div className="text-xs font-mono uppercase text-neutral-500">Entrega estimada</div>
              <div className="font-display text-2xl mt-1">{order.delivery_estimate || "—"}</div>
              {order.delivery_method !== "sucursal" && order.shipping && (
                <>
                  <div className="text-xs text-neutral-500 mt-3 pt-3 border-t border-neutral-200 font-mono uppercase">Dirección</div>
                  <div className="text-sm mt-1">{order.shipping.name}</div>
                  <div className="text-xs text-neutral-600">{order.shipping.address}</div>
                  <div className="text-xs text-neutral-600">
                    {[order.shipping.city, order.shipping.state, order.shipping.zip].filter(Boolean).join(", ")}
                  </div>
                  {order.shipping.phone && <div className="text-xs text-neutral-600 mt-1 font-mono">{order.shipping.phone}</div>}
                </>
              )}
              {order.delivery_method === "sucursal" && (
                <div className="text-xs text-neutral-600 mt-2">Recolección en sucursal</div>
              )}
            </div>

            {/* Reminder */}
            {!approved && (
              <div className="bg-neutral-900 text-white p-5" data-testid="public-order-reminder">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="text-xs leading-relaxed">
                    <span className="font-bold">Recuerda autorizar tu pedido.</span> Sin la autorización no podemos iniciar producción. Revisa los detalles y pulsa <span className="font-mono">Autorizar pedido</span>.
                  </div>
                </div>
              </div>
            )}

            {/* Contact */}
            {(settings?.company_whatsapp || settings?.company_phone) && (
              <div className="bg-white border-2 border-black p-5">
                <div className="text-xs font-mono uppercase text-neutral-500">¿Dudas o cambios?</div>
                <div className="text-sm mt-2">Contáctanos por WhatsApp</div>
                <a
                  href={`https://wa.me/${(settings.company_whatsapp || settings.company_phone).replace(/\D/g, "")}?text=${encodeURIComponent(`Hola, sobre mi pedido #${order.order_number}...`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-sm underline"
                  data-testid="public-order-whatsapp-link"
                >
                  {settings.company_whatsapp || settings.company_phone}
                </a>
              </div>
            )}

            {/* Special promos — placed under "Dudas o cambios" for high visibility */}
            <SpecialPromosBlock />
          </div>
        </div>

        <footer className="mt-12 pt-8 border-t border-neutral-200 text-center text-xs text-neutral-500 font-mono space-y-3">
          <div>
            Este panel es privado para tu pedido #{order.order_number}. No compartas el enlace con terceros.
          </div>
          <div className="pt-3">
            <div className="text-[10px] uppercase text-neutral-400 mb-1">¿Quieres ver más productos?</div>
            <a
              href="https://printecdelnorte.com/tienda"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-black hover:text-[#FF3B30] underline normal-case font-medium"
              data-testid="public-order-catalog-link"
            >
              Visita nuestro catálogo de productos → printecdelnorte.com/tienda
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}

// --- Special promos / featured products from the shop (keeps customers engaged
// while they wait to authorize their order).
function SpecialPromosBlock() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const catalogUrl = "https://printecdelnorte.com/tienda";

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products`);
        const items = Array.isArray(data) ? data : (data.items || []);
        // Prefer featured products if any, fall back to first ones
        const featured = items.filter((p) => p.is_featured || p.featured);
        const picked = (featured.length ? featured : items).slice(0, 4);
        if (active) setProducts(picked);
      } catch (_e) {
        if (active) setProducts([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <div className="bg-white border-2 border-black p-6 relative overflow-hidden" data-testid="public-order-promos-block">
      <div className="absolute top-0 right-0 bg-[#FF3B30] text-white text-[10px] font-mono uppercase px-3 py-1">
        Promos
      </div>
      <div className="flex items-start gap-3 mb-1">
        <div className="w-10 h-10 bg-[#FF3B30] text-white flex items-center justify-center font-display text-xl flex-shrink-0">★</div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl tracking-tight">Promociones Especiales</h2>
          <p className="text-xs text-neutral-500">Descubre más productos de nuestra tienda</p>
        </div>
      </div>

      {loading ? (
        <div className="h-56 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : products.length === 0 ? (
        <div className="h-56 flex items-center justify-center text-sm text-neutral-500 border border-dashed border-neutral-300">
          No hay productos para mostrar.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 mt-4">
          {products.map((p) => (
            <a
              key={p.product_id}
              href={catalogUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group border-2 border-black bg-white hover:border-[#FF3B30] transition-colors flex flex-col"
              data-testid={`public-order-promo-${p.product_id}`}
            >
              <div className="w-full aspect-square bg-neutral-50 overflow-hidden">
                {p.base_image_url ? (
                  <img
                    src={p.base_image_url}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-300 text-xs font-mono">—</div>
                )}
              </div>
              <div className="p-2 flex flex-col flex-1">
                <div className="font-medium text-[11px] leading-tight line-clamp-2 min-h-[2.2em]">{p.name}</div>
                <div className="mt-1 flex items-baseline justify-between gap-1">
                  <div className="font-display text-base leading-none">{currency(p.base_price)}</div>
                  <div className="text-[9px] font-mono uppercase text-neutral-500 group-hover:text-[#FF3B30] underline">Ver</div>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}

      <a
        href={catalogUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 flex items-center justify-center gap-2 bg-black text-white hover:bg-[#FF3B30] h-11 text-sm font-medium transition-colors"
        data-testid="public-order-promos-shop-link"
      >
        Ver toda la tienda →
      </a>
    </div>
  );
}

// --- Small bank account row with copy button ---
function BankRow({ label, value, testid, mono }) {
  const copy = () => {
    navigator.clipboard.writeText(String(value)).then(
      () => toast.success(`${label} copiado`),
      () => toast.error("No se pudo copiar")
    );
  };
  return (
    <div className="border border-neutral-200 bg-neutral-50 p-2.5" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase text-neutral-500">{label}</div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <div className={`text-sm break-all ${mono ? "font-mono" : "font-medium"}`}>{value}</div>
        <button
          onClick={copy}
          className="text-[10px] font-mono uppercase text-neutral-500 hover:text-black underline flex-shrink-0"
          data-testid={`${testid}-copy`}
        >
          Copiar
        </button>
      </div>
    </div>
  );
}

// --- Attachments uploader: 2 slots (A & B) + optional measurements per slot ---
function AttachmentsBlock({ order, orderNumber, approved, onUpdate }) {
  const attachments = order.customer_attachments || [];
  const slotA = attachments.find((a) => a.slot === "A");
  const slotB = attachments.find((a) => a.slot === "B");

  return (
    <div className="bg-white border-2 border-black p-6" data-testid="public-order-attachments">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h2 className="font-display text-xl tracking-tight">Adjunta tu diseño</h2>
        {!approved && (
          <span className="text-[10px] font-mono uppercase bg-amber-100 text-amber-900 border border-amber-300 px-1.5 py-0.5">
            Opcional · antes de autorizar
          </span>
        )}
      </div>
      <p className="text-xs text-neutral-500 mb-4">
        Sube el archivo final de tu diseño en una o dos cajas y, si aplica, escribe las medidas exactas. Esto nos ayuda a imprimir sin errores.
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        <AttachmentSlot slot="A" label="Caja A" item={slotA} orderNumber={orderNumber} approved={approved} onUpdate={onUpdate} attachmentIndex={slotA ? attachments.indexOf(slotA) : null} />
        <AttachmentSlot slot="B" label="Caja B" item={slotB} orderNumber={orderNumber} approved={approved} onUpdate={onUpdate} attachmentIndex={slotB ? attachments.indexOf(slotB) : null} />
      </div>
    </div>
  );
}

function AttachmentSlot({ slot, label, item, orderNumber, approved, onUpdate, attachmentIndex }) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [measurements, setMeasurements] = useState("");
  const [note, setNote] = useState("");
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file || approved) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("El archivo excede 15 MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("slot", slot);
      if (measurements.trim()) form.append("measurements", measurements.trim());
      if (note.trim()) form.append("note", note.trim());
      const { data } = await axios.post(
        `${API}/public/orders/by-number/${orderNumber}/attachments`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      onUpdate(data);
      setMeasurements("");
      setNote("");
      toast.success(`Archivo subido a ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async () => {
    if (attachmentIndex === null || approved) return;
    if (!window.confirm(`¿Eliminar el archivo de ${label}?`)) return;
    setRemoving(true);
    try {
      const { data } = await axios.delete(
        `${API}/public/orders/by-number/${orderNumber}/attachments/${attachmentIndex}`
      );
      onUpdate(data);
      toast.success("Archivo eliminado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setRemoving(false);
    }
  };

  const isImg = item && (item.content_type || "").startsWith("image/");
  const fileUrl = item ? `${API}/files/${item.storage_path}` : null;

  if (item) {
    return (
      <div className="border-2 border-emerald-600 bg-emerald-50 p-3" data-testid={`public-attachment-slot-${slot}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-mono uppercase font-bold text-emerald-900">{label} · Subido ✓</div>
          {!approved && (
            <button
              onClick={removeFile}
              disabled={removing}
              className="text-emerald-700 hover:text-[#FF3B30] disabled:opacity-50"
              data-testid={`public-attachment-remove-${slot}`}
              title="Eliminar y volver a subir"
            >
              {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          )}
        </div>
        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="block">
          <div className="w-full h-36 bg-white border-2 border-emerald-300 flex items-center justify-center overflow-hidden">
            {isImg ? (
              <img src={fileUrl} alt={item.filename} className="max-w-full max-h-full object-contain" style={{ mixBlendMode: "multiply" }} />
            ) : (
              <div className="text-center">
                <FileImage className="w-10 h-10 mx-auto text-emerald-600" />
                <div className="text-[10px] font-mono mt-1">{item.filename}</div>
              </div>
            )}
          </div>
        </a>
        <div className="text-[11px] font-mono text-emerald-800 mt-2 truncate">{item.filename}</div>
        {item.measurements && (
          <div className="text-[11px] mt-1 bg-white border border-emerald-300 p-1.5">
            <span className="font-mono uppercase text-[9px] text-emerald-700">Medidas</span>
            <div className="text-neutral-800 whitespace-pre-wrap">{item.measurements}</div>
          </div>
        )}
        {item.note && (
          <div className="text-[11px] italic text-neutral-600 mt-1">"{item.note}"</div>
        )}
      </div>
    );
  }

  return (
    <div className={`border-2 border-dashed p-3 ${approved ? "border-neutral-200 bg-neutral-50 opacity-60" : "border-black bg-white"}`} data-testid={`public-attachment-slot-${slot}`}>
      <div className="text-[10px] font-mono uppercase font-bold text-neutral-700 mb-2">{label}</div>
      <label
        className={`w-full h-32 bg-neutral-50 border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center gap-1 cursor-pointer transition-colors ${approved || uploading ? "pointer-events-none opacity-60" : "hover:border-black hover:bg-neutral-100"}`}
        data-testid={`public-attachment-dropzone-${slot}`}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf,.ai,.psd,.svg"
          className="sr-only"
          onChange={(e) => handleFile(e.target.files?.[0])}
          disabled={approved || uploading}
          data-testid={`public-attachment-input-${slot}`}
        />
        {uploading ? (
          <>
            <Loader2 className="w-6 h-6 animate-spin text-neutral-500" />
            <div className="text-[11px] text-neutral-500">Subiendo…</div>
          </>
        ) : (
          <>
            <Upload className="w-6 h-6 text-neutral-500" />
            <div className="text-xs font-medium">Elegir archivo</div>
            <div className="text-[10px] font-mono text-neutral-400">PNG · JPG · PDF · hasta 15 MB</div>
          </>
        )}
      </label>
      <div className="mt-2">
        <label className="text-[10px] font-mono uppercase text-neutral-500 flex items-center justify-between">
          Medidas (opcional)
          <span className="text-[9px] text-neutral-400 normal-case">ej. 10cm × 15cm</span>
        </label>
        <textarea
          value={measurements}
          onChange={(e) => setMeasurements(e.target.value)}
          disabled={approved || uploading}
          placeholder="Alto × ancho, ubicación, color exacto…"
          rows={2}
          className="w-full border border-neutral-300 px-2 py-1 text-xs outline-none focus:border-black resize-none disabled:bg-neutral-100"
          data-testid={`public-attachment-measurements-${slot}`}
        />
      </div>
      <div className="mt-2">
        <label className="text-[10px] font-mono uppercase text-neutral-500">Nota (opcional)</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={approved || uploading}
          placeholder="Comentario para producción"
          className="w-full border border-neutral-300 px-2 py-1 text-xs outline-none focus:border-black disabled:bg-neutral-100"
          data-testid={`public-attachment-note-${slot}`}
        />
      </div>
    </div>
  );
}
